import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import {
	openDialog,
	logOut,
	logOutReasons,
	openDrawer,
	closeDrawer,
} from 'stash/actions';
import DrawerPortal from 'stash/components/drawer/DrawerPortal';
import NotificationAvatar from 'stash/assets/top-nav/notification-avatar.svg';
import DefaultAvatar from 'stash/assets/top-nav/default-avatar.svg';
import { BanksyImage } from 'stash/components/global/BanksyImage';
import { userAvatarUrlSelector, userSelector } from 'stash/selectors/user';

type ContentMoatNavbarUser = {
	first_name?: string;
	last_name?: string;
	created_at?: string | Date;
};

export type ContentMoatNavbarProps = {
	open: (e: React.MouseEvent) => void;
	close: () => void;
	drawerOpen: boolean;
	logOut: () => void;
	user: ContentMoatNavbarUser;
	avatar: string;
};

export const ContentMoatNavbar = ({
	open,
	close,
	drawerOpen,
	logOut: handleLogOut,
	user,
	avatar,
}: ContentMoatNavbarProps) => {
	return (
		<>
			<div className="w-full px-content2XS py-contentSmall">
				<div className="mx-auto flex max-w-[1280px] items-center justify-between">
					<div>
						<Link to="/" aria-label="Stash Logo">
							<BanksyImage
								src="/images/logos/stashS/stashS.png"
								alt="Stash Logo"
								className="relative z-[1] h-[1.3rem] w-auto"
							/>
						</Link>
					</div>
					<button
						type="button"
						className="cursor-pointer border-0 bg-transparent p-0"
						onClick={open}
						aria-label="Account settings"
					>
						<img
							className="h-9 w-9"
							src={NotificationAvatar}
							alt="Account settings"
						/>
					</button>
				</div>
			</div>
			<DrawerPortal
				active={drawerOpen}
				close={close}
				logOut={handleLogOut}
				user={user}
				avatar={avatar}
			/>
		</>
	);
};

const ContentMoatNavbarContainer = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const drawerOpen = useSelector(
		(state: { application: { drawer: boolean } }) => state.application.drawer
	);
	const user = useSelector(userSelector) as ContentMoatNavbarUser;
	const avatarUrl = useSelector(userAvatarUrlSelector) as string | undefined;
	const avatar = avatarUrl || DefaultAvatar;

	const open = (e: React.MouseEvent) => {
		const openSideDrawer = () => {
			e.preventDefault();
			dispatch(openDrawer());
		};

		if (window.innerWidth > 767) {
			history.push('/account-settings');
		} else {
			openSideDrawer();
		}
	};

	const close = () => {
		dispatch(closeDrawer());
	};

	const handleLogOut = () => {
		(dispatch as (action: unknown) => Promise<unknown>)(
			openDialog({
				prompt: 'Are you sure you want to log out?',
				confirmText: 'Log me out',
				cancelText: 'Cancel',
			})
		)
			.then(() => dispatch(logOut(logOutReasons.LOGGED_OUT)))
			.catch(() => {});
	};

	return (
		<ContentMoatNavbar
			open={open}
			close={close}
			drawerOpen={drawerOpen}
			logOut={handleLogOut}
			user={user}
			avatar={avatar}
		/>
	);
};

export default ContentMoatNavbarContainer;
